// Inline brand-name component for body copy. Fixes the I/l visual ambiguity
// surfaced in the Home redesign round-2 polish sweep (KAN-25020 P1-1):
// "AIghtBet" rendered in the body display font reads as "AlghtBet" because
// the capital I has no serifs in Geist / IBM Plex Sans / Inter / SF Pro
// Display at body-text sizes. The full SVG wordmark at `./brand/Wordmark.tsx`
// solves this for headers and marketing surfaces, but is too heavy for
// inline use (paragraphs, list items, button labels, toast strings, etc.).
//
// This component is the inline counterpart: it renders "AIghtBet Sports" as
// a styled text span where the "AI" prefix is brand-orange + 800 weight so
// the reader's eye latches onto it as a wordmark cue. Bold-orange "AI"
// inline can NEVER be misread as "Al" because lowercase l is never bold-
// orange in body copy. Same letters, different visual treatment, brand
// canon preserved.
//
// `variant`:
//   - `inline`  → body-copy span; "AI" styled, rest inherits font/size.
//                 Use inside paragraphs, list items, button labels.
//   - `header`  → same shape, but the "AI" is rendered slightly larger
//                 (1.05em) to read as a header-class brand cue. Still
//                 baseline-safe.
//   - `wordmark` → delegates to the full SVG <Wordmark/> lockup. Use on
//                  marketing pages, login screen, mobile header.
//
// `short`:  omit " Sports" — for the rare case where the surrounding copy
//            already has "Sports" or where only "AIghtBet" is the right
//            string ("AIghtBet has no relationship with…").
//
// `suffix`: append a fixed suffix after " Sports" — e.g. "LLC" so the
//            component renders "AIghtBet Sports LLC" as a single brand
//            unit. Suffix renders in plain inherited weight, so the brand
//            cue stays on the AI prefix.
//
// Cross-platform parity: the iOS native counterpart at
// `apps/ios/components/BrandName.tsx` uses the same prop shape and the
// same color treatment so the same JSX intent renders the same brand
// presentation on aightbetsports.com (web) and inside the iOS app.

import React from 'react';
import Wordmark from './brand/Wordmark';

const BRAND_ORANGE = '#FB923C';

export interface BrandNameProps {
  variant?: 'inline' | 'header' | 'wordmark';
  short?: boolean;
  suffix?: string;
  className?: string;
}

export const BrandName: React.FC<BrandNameProps> = ({
  variant = 'inline',
  short = false,
  suffix,
  className,
}) => {
  if (variant === 'wordmark') {
    return <Wordmark className={className} />;
  }

  const aiStyle: React.CSSProperties = {
    color: BRAND_ORANGE,
    fontWeight: 800,
    fontSize: variant === 'header' ? '1.05em' : 'inherit',
  };

  const rest = `ghtBet${short ? '' : ' Sports'}${suffix ? ` ${suffix}` : ''}`;

  return (
    <span className={className} style={{ whiteSpace: 'nowrap' }}>
      <span style={aiStyle}>AI</span>
      {rest}
    </span>
  );
};

export default BrandName;
